import { useState } from 'react';

export interface AnnouncementFormUIState {
  selectedFloorValues: any;
  selectedBuildings: any[];
  selectedUnitValues: any[];

  buildingOptions: any[];
  levelsOptions: any[];
  unitOptions: any[];

  hydratedPropertyId: string | null;

  plan: string;

  description: any;

  // The Multiselect widget is uncontrolled — it only reads selectedValues at
  // mount — so the views key off these. Bumping a key forces React to remount
  // that widget, which re-seeds it from our current state. One per scope so we
  // can resync, say, the unit list (when its source scope changes) without
  // remounting the building list the user is mid-interaction with.
  buildingRemountKey: number;
  floorRemountKey: number;
  unitRemountKey: number;
}

export interface RemountScopes {
  building?: boolean;
  floor?: boolean;
  unit?: boolean;
}

export interface UseAnnouncementFormUIStateReturn {
  uiState: AnnouncementFormUIState;
  patchUiState: (partial: Partial<AnnouncementFormUIState>) => void;
  remountMultiselects: (scopes: RemountScopes) => void;
}

const INITIAL_REMOUNT_KEY = 0;
const REMOUNT_KEY_INCREMENT = 1;

const nextRemountKey = (current: number, shouldRemount?: boolean): number => {
  const safeCurrent = current ?? INITIAL_REMOUNT_KEY;

  if (!shouldRemount) {
    return safeCurrent;
  }

  return safeCurrent + REMOUNT_KEY_INCREMENT;
};

export const useAnnouncementFormUIState = (
  initial?: Partial<AnnouncementFormUIState>
): UseAnnouncementFormUIStateReturn => {
  const [uiState, setUiState] = useState<AnnouncementFormUIState>(() => ({
    selectedFloorValues: [],
    selectedBuildings: [],
    selectedUnitValues: [],

    buildingOptions: [],
    levelsOptions: [],
    unitOptions: [],

    hydratedPropertyId: null,

    plan: 'buildings',

    description: undefined,

    buildingRemountKey: INITIAL_REMOUNT_KEY,
    floorRemountKey: INITIAL_REMOUNT_KEY,
    unitRemountKey: INITIAL_REMOUNT_KEY,

    ...initial,
  }));

  const patchUiState = (partial: Partial<AnnouncementFormUIState>) => {
    setUiState((prev: AnnouncementFormUIState) => ({ ...prev, ...partial }));
  };

  const remountMultiselects = (scopes: RemountScopes) => {
    setUiState((prev: AnnouncementFormUIState) => ({
      ...prev,
      buildingRemountKey: nextRemountKey(prev.buildingRemountKey, scopes.building),
      floorRemountKey: nextRemountKey(prev.floorRemountKey, scopes.floor),
      unitRemountKey: nextRemountKey(prev.unitRemountKey, scopes.unit),
    }));
  };

  return { uiState, patchUiState, remountMultiselects };
};
